import { useNavigate, useLocation, useOutletContext } from 'react-router-dom'
import { useState } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { nextPage } from 'components/RCPro/helpers/nav'
import classNames from 'classnames'
import FirstName from 'components/RCPro/components/form/FirstName/FirstName'
import LastName from 'components/RCPro/components/form/LastName/LastName'
import SelectCompany from 'components/RCPro/components/form/SelectCompany/SelectCompany'
import Activity from 'components/RCPro/components/form/Activity/Activity'
import Email from 'components/RCPro/components/form/Email/Email'
import PhoneNumber from 'components/RCPro/components/form/PhoneNumber/PhoneNumber'
import Select from 'components/common/Select/Select'
import Link from 'components/common/Link/Link'
import ArrowAlt from 'components/assets/icons/ArrowAlt'
import CircleOk from 'components/assets/icons/CircleOk'
import Button from 'components/common/Button/Button'
import { formParams } from 'constants/inputValidation'
import style from './Estimate.module.scss'
import SideForm from 'components/RCPro/components/SideForm/SideForm'
import Turnover from 'components/RCPro/components/form/Turnover/Turnover'
import Input from 'components/common/Input/Input'
import InputError from 'components/common/InputError/InputError'
import { formatNb } from 'functions'
import { usePrice } from 'components/RCPro/components/Context/Context'
import Eye from 'components/assets/icons/Eye'
import Loader from 'components/assets/icons/Loader'
import ResponseMessage from 'components/common/ResponseMessage/ResponseMessage'
import api from 'helpers/api'

const RCProEstimate = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { state, pathname } = useLocation()
  const { nextPageTransition } = useOutletContext()
  const { price: { LOI }, getPrice } = usePrice()
  const [ edit, setEdit ] = useState(false)
  const [ openBasket, setOpenBasket ] = useState(false)
  const { estimate, activity, turnover, resume } = state.texts
  const [ loading, setLoading ] = useState(false)
  const [ errorMessage, setErrorMessage ] = useState(false)
  const other = t('common:other')

  const numberToPrice = (nb) => Number.parseFloat(nb).toFixed(2).replace('.',',')+" €"

  // Initialize form
  const {
    register,
    formState: { isValid, errors },
    handleSubmit,
    trigger,
    setValue,
    control,
    watch
  } = useForm({
    ...formParams,
    values: {
      firstname: state.firstname,
      lastname: state.lastname,
      organizationName: state.organizationName,
      companyImmatriculation: state.companyImmatriculation,
      headquarterAddress: state.headquarterAddress,
      headquarterCity: state.headquarterCity,
      headquarterZipcode: state.headquarterZipcode,
      sector: state.sector,
      codeProduct: state.codeProduct,
      activity: state.activity,
      turnover: state.turnover.toString(),
      email: state.email,
      phone: state.phone,
      textActivityDocument: state.textActivityDocument
    },
  })

  const activities = activity?.domains?.find( domain => domain.sector === watch('sector') )?.activities || []
  const { ipid, ni } = resume?.exclusion?.find( codeProduct => codeProduct.name === state.codeProduct ) || {}

  const toggleEdit = () => {
    setEdit(!edit)
  }

  const onCompanyChange = datas => {
    setValue('organizationName', datas.organizationName)
    setValue('companyImmatriculation', datas.companyImmatriculation || '')
    setValue('headquarterAddress', datas.headquarterAddress || '')
    setValue('headquarterCity', datas.headquarterCity || '')
    setValue('headquarterZipcode', datas.headquarterZipcode || '')
    trigger()
  }

  const onDomainChange = datas => {
    const { sector } = activity?.domains?.find( domain => domain.sector === datas )
    setValue('sector', sector)
    setValue('codeProduct', '')
    setValue('textActivityDocument', '')
    setValue('activity', '')
    trigger()
  }

  const onActivityChange = datas => {
    const { codeProduct, textActivityDocument } = activities.find( activity => activity.name === datas ) || {}
    setValue('activity', datas)
    setValue('codeProduct', codeProduct)
    setValue('textActivityDocument', textActivityDocument)
    trigger()
  }

  const subscribe = () => {
    nextPage({ pathname, state, navigate })
    nextPageTransition()
  }

  const onSubmit = handleSubmit(async data => {
    try{
      setLoading(true)
      setErrorMessage(false)
      Object.keys(data).map( key => {
        if (['number', 'string'].includes(typeof data[key]))
        data[key] = data[key].trim()
        return ''
      })
      const CA = data.turnover.replace(/\D/g, '')
      const { amount, exclusion, sign } = turnover?.exclusion || {}
      const excludedDomain = activity?.domains?.find( domain => domain.sector === data.sector )?.exclusion
      let outForMargin = false
      if (exclusion && sign && !isNaN(amount)){
        switch(sign){
          case 'supérieur':
            outForMargin = CA > amount;
            break;
          case 'supérieur égale':
            outForMargin = CA >= amount;
            break;
          case 'inférieur':
            outForMargin = CA < amount;
            break;
          case 'inférieur égale':
            outForMargin = CA <= amount;
            break;
          case 'égale':
            outForMargin = CA === amount;
            break;
          default:
            outForMargin = false
            break;
        }
      }

      const res = await api.get({
        url: `/RCPro/${state.contractType}/${data.companyImmatriculation}`,
        headers: { Authorization: `Bearer ${state.token}` }
      })
      setLoading(false)

      let oups = ''
      if (outForMargin){
        oups = 'high-turnover'
      }
      if (CA <= 70000 && data.codeProduct === "TECH"){
        oups = 'low-turnover'
      }
      if (data.activity === 'Autre' || excludedDomain){
        oups = 'activity'
      }
      if (res?.adhesion){
        oups = 'registered-company'
      }
      if (data.headquarterZipcode.match(/^98[78]/)){
        oups = 'outside-company'
      }

      if (oups) {
        setTimeout( () => {
          navigate('../oups?reason='+oups, { state })
        }, 250)
      } else {
        getPrice({
          codeProduct: watch('codeProduct'),
          turnover: CA,
          activity: data.activity,
          token: state.token
        })
        setTimeout( () => {
          navigate('../loading?reason=updating', { state: { ...state, ...data, turnover: CA } })
        }, 250)
      }
      toggleEdit()
      nextPageTransition()
    } catch (err) {
      console.log('Check company adhesion error')
      setLoading(false)
      setErrorMessage(true)
    }
  })

  return (
    <>
      <span className={style.message}>
        <CircleOk />
        {estimate.success}
      </span>

      <span className={style.clear}></span>

      <aside className={classNames(style.basket, openBasket && style.open)}>
        <span className={style.toggle} onClick={() => setOpenBasket(!openBasket)}>
          <ArrowAlt />
        </span>
        <h2>{estimate.basketTitle} <span className={style.quantity}>(1)</span></h2>
        <div className={style.inside}>
          <ul>
            <li>
              <span>{estimate.productName}</span>
              <span>{numberToPrice(state.price)}</span>
            </li>
          </ul>
          <span className={style.total}>{formatNb(numberToPrice(state.price))}/mois</span>
          <Button
            label={estimate.basketButton}
            onClick={subscribe}
            type="primary"
          />
        </div>
      </aside>

      <div className={style.estimate}>
        <h1 className={style.title}>{estimate.title}</h1>
        <p>
          <span dangerouslySetInnerHTML={{__html:
            estimate.resume
            .replace('{{activity}}', `<b>${state.activity}</b>`)
            .replace('{{turnover}}', `<b>${formatNb(state.turnover)}&nbsp;€</b>`)
          }}></span>
          &nbsp;
          <Link
            label={estimate.error}
            className={style.link}
            to={''}
            onClick={toggleEdit}
          />
        </p>

        <section className={style.product}>
          <h2>{estimate.productName}</h2>
          <p>{estimate.description}</p>
          <span className={style.price}>{formatNb(numberToPrice(state.price))}/mois</span>
          <span>{estimate?.LOI?.replace('{{LOI}}', formatNb(LOI || (Number(state.turnover) < 300000 ? 100000 : 500000)))}</span>

          <ul>
            {estimate.guarantees?.map( (elm, index) => (
              <li key={`elm-${index}`}>
                <div dangerouslySetInnerHTML={{__html: elm.icon}}></div>
                <h3>{elm.title}</h3>
                <p>{elm.text}</p>
              </li>
            ))}
          </ul>
        </section>

        <div className={style.bloc}>
          <h2>{resume?.documents}</h2>
          <ul className={style.documents}>
            <li>
              {estimate?.notice}
              <Link
                label={<><Eye />{resume?.read}</>}
                target='_blank'
                to={ni}
              />
            </li>
            <li>
              {estimate?.fiche}
              <Link
                label={<><Eye />{resume?.read}</>}
                target='_blank'
                to={ipid}
              />
            </li>
          </ul>
        </div>
      </div>


      <SideForm
        title={estimate.editTitle}
        toggleEdit={toggleEdit}
        onSubmit={onSubmit}
        active={edit}
        content={
          <FormProvider {...{register, errors}}>
            <FirstName
              register={register}
              error={errors.firstname}
            />

            <LastName
              register={register}
              error={errors.lastname}
            />

            <SelectCompany
              onChange={onCompanyChange}
              value={watch('organizationName')}
            />

            <Select
              options={activity?.domains?.map( domain => ({
                label: domain.sector,
                value: domain.sector
              }))}
              onChange={val => onDomainChange(val.value)}
              label={t('mainReporting:form.domain.label')}
              placeholder={t('mainReporting:form.domain.placeholder')}
              value={watch('sector')}
              styles={{
                control:{
                  background: '#FFF'
                }
              }}
            />

            <Activity
              activities={activities}
              onChange={onActivityChange}
              value={watch('activity')}
            />

            {watch('activity') === other && <>
              <Input
                error={errors.special}
                register={register('special', {
                  validate: {
                    required: value => {
                      return !value && watch('activity') === other ? t('mainReporting:form.mandatory') : true
                    },
                  }
                })}
                label={activity?.specialLabel}
                placeholder={activity?.specialPlaceholder}
              />
              <InputError error={errors.special} />
            </>}

            <Turnover
              register={register}
              error={errors.turnover}
              value={watch('turnover')}
            />

            <Email
              register={register}
              error={errors.email}
            />

            <PhoneNumber
              control={control}
              error={errors.phone}
              value={state?.phone?.number || ''}
            />

            {errorMessage && <ResponseMessage className={style.response} label={t('common:form.genericError')} />}

            <Button
              label={loading ? <Loader/> : estimate.editButton}
              onClick={handleSubmit}
              disabled={!isValid || !watch('activity') || loading}
              type={loading ? 'loading' : 'primary'}
            />
          </FormProvider>
        }
      />
    </>
  )
}

export default RCProEstimate